// Small, dependency-free export helpers for the damper charts and KPI table.
//
// exportChartPng lifts a uPlot instance's backing canvas to a PNG download.
// exportTableCsv serialises rows to RFC-4180 CSV and downloads them. The CSV
// escaping (toCsv / csvEscapeField) is a pure function so it can be asserted
// exactly; the download side (an anchor click) is a thin, browser-only wrapper
// guarded so it never throws in the UI.

// Minimal structural view of a uPlot instance: enough to reach the canvas
// without importing uPlot's types here. Real instances expose `ctx` (the 2D
// context) and `root` (the wrapper element that holds the <canvas>).
export type CanvasChart = {
  ctx?: { canvas?: HTMLCanvasElement | null } | null;
  root?: HTMLElement | null;
};

export type CsvCell = string | number | boolean | null | undefined;

// Quote a field when it contains a comma, double-quote, or newline; escape
// internal double-quotes by doubling them (RFC 4180). null/undefined render as
// an empty field.
export function csvEscapeField(value: CsvCell): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// Serialise a 2D array of cells to CSV text. Rows are CRLF-separated per the
// spec; each row is a comma-join of escaped fields.
export function toCsv(rows: ReadonlyArray<ReadonlyArray<CsvCell>>): string {
  return rows.map((row) => row.map(csvEscapeField).join(",")).join("\r\n");
}

// Resolve the backing <canvas> of a uPlot-like instance.
function chartCanvas(chart: CanvasChart | null | undefined): HTMLCanvasElement | null {
  if (!chart) return null;
  const direct = chart.ctx?.canvas ?? null;
  if (direct) return direct;
  return chart.root?.querySelector("canvas") ?? null;
}

// Trigger a browser download for an href. Guarded: a headless/JSDOM environment
// without a live document simply no-ops rather than throwing.
function triggerDownload(href: string, filename: string): void {
  try {
    if (typeof document === "undefined") return;
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch {
    // Download is a best-effort convenience; never let it break the page.
  }
}

// Export a chart's canvas as a PNG download. Returns true when a canvas was
// found and the download was attempted, false when the instance had no canvas.
export function exportChartPng(
  chart: CanvasChart | null | undefined,
  filename: string,
): boolean {
  const canvas = chartCanvas(chart);
  if (!canvas || typeof canvas.toDataURL !== "function") return false;
  let url: string;
  try {
    url = canvas.toDataURL("image/png");
  } catch {
    return false;
  }
  triggerDownload(url, filename);
  return true;
}

// Export rows as a CSV download. Uses a data URI so it works without
// URL.createObjectURL. Returns the CSV text (handy for tests + callers).
export function exportTableCsv(
  rows: ReadonlyArray<ReadonlyArray<CsvCell>>,
  filename: string,
): string {
  const csv = toCsv(rows);
  const href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  triggerDownload(href, filename);
  return csv;
}
